import { useEffect, useRef } from 'react';
import useExitIntent from '@/app/hooks/useExitIntent';
import { leaveTriggersFor, watchInactivity } from '@/app/lib/guideChat/triggers';
import type { OpenerTrigger } from '@/app/lib/guideChat/types';
import { deviceClass } from './pageContext';

const READ_TIME_MS = 35_000;
const LANDING_TIME_MS = 12_000;
const FAQ_HOVER_MS = 1_500;
const FAQ_SELECTOR = 'details, summary, [id*="faq" i], [class*="faq" i], a[href*="simulation-assurance-pret"], a[href*="devis-assurance-pret"]';

interface Options {
  enabled: boolean;
  kind: 'article' | 'landing';
  onTrigger: (trigger: OpenerTrigger) => void;
}

// §4.2: first signal wins (scroll > 50 % or reading time, FAQ/simulator dwell, then exit intent on desktop or inactivity on mobile), one opener per page view.
export function useContentPageTriggers({ enabled, kind, onTrigger }: Options) {
  const onTriggerRef = useRef(onTrigger);
  const firedRef = useRef(false);

  useEffect(() => {
    onTriggerRef.current = onTrigger;
  }, [onTrigger]);

  useEffect(() => {
    if (!enabled) return;
    firedRef.current = false;
    const cleanups: Array<() => void> = [];
    const fire = (trigger: OpenerTrigger) => {
      if (firedRef.current) return;
      firedRef.current = true;
      cleanups.splice(0).forEach((fn) => fn());
      onTriggerRef.current(trigger);
    };
    const timer = (ms: number, trigger: OpenerTrigger) => {
      const id = window.setTimeout(() => fire(trigger), ms);
      cleanups.push(() => window.clearTimeout(id));
    };
    const listen = (target: Window | Document, event: string, handler: EventListener, opts?: AddEventListenerOptions) => {
      target.addEventListener(event, handler, opts);
      cleanups.push(() => target.removeEventListener(event, handler, opts));
    };

    if (kind === 'landing') {
      timer(LANDING_TIME_MS, 'landing_time');
    } else {
      timer(READ_TIME_MS, 'read_time');
      listen(window, 'scroll', () => {
        const height = document.documentElement.scrollHeight - window.innerHeight;
        if (height > 0 && window.scrollY / height > 0.5) fire('scroll');
      }, { passive: true });

      let hoverTimer: number | null = null;
      listen(document, 'mouseover', (e) => {
        if (!(e.target as Element | null)?.closest?.(FAQ_SELECTOR)) return;
        if (hoverTimer) window.clearTimeout(hoverTimer);
        hoverTimer = window.setTimeout(() => fire('faq_hover'), FAQ_HOVER_MS);
      });
      listen(document, 'mouseout', (e) => {
        if (!(e.target as Element | null)?.closest?.(FAQ_SELECTOR) || !hoverTimer) return;
        window.clearTimeout(hoverTimer);
        hoverTimer = null;
      });
      cleanups.push(() => hoverTimer && window.clearTimeout(hoverTimer));
      listen(document, 'click', (e) => {
        if ((e.target as Element | null)?.closest?.('summary, [id*="faq" i], [class*="faq" i]')) fire('faq_hover');
      });
    }

    if (leaveTriggersFor(kind, deviceClass()).includes('inactivity')) {
      cleanups.push(watchInactivity(window, () => fire('inactivity')));
    }

    return () => cleanups.splice(0).forEach((fn) => fn());
  }, [enabled, kind]);

  useExitIntent(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    onTriggerRef.current('exit_intent');
  }, enabled && leaveTriggersFor(kind, deviceClass()).includes('exit_intent'));
}
